/**
 * Echo Application Configuration
 * Environment-specific configuration for GitHub workflow dispatch
 */

export interface EchoConfig {
  GITHUB_OWNER: string;
  GITHUB_REPO: string;
  GITHUB_WORKFLOW: string;
  GITHUB_REF: string;
  GITHUB_TOKEN: string;
  S3_BUCKET: string;
}

/**
 * Get Echo configuration from environment variables
 */
export function getEchoConfig(): EchoConfig {
  return {
    GITHUB_OWNER: import.meta.env.VITE_GITHUB_OWNER || 'fti-sc',
    GITHUB_REPO:
      import.meta.env.VITE_GITHUB_REPO || 'Kick-Stream-Utilities',
    GITHUB_WORKFLOW: import.meta.env.VITE_GITHUB_WORKFLOW || 'transcribe.yaml',
    GITHUB_REF: import.meta.env.VITE_GITHUB_REF || 'main',
    GITHUB_TOKEN: import.meta.env.VITE_GITHUB_TOKEN || '',
    S3_BUCKET:
      import.meta.env.VITE_S3_BUCKET || 'fti-video-stream-transcripts-prod',
  };
}

/**
 * Default prompt template for transcription
 */
export const DEFAULT_PROMPT_TEMPLATE = 'Summarize the following audio or video.';

/**
 * Master prompt prefix for combining segment summaries
 */
export const MASTER_PROMPT_PREFIX =
  'You are combining segment summaries into a final comprehensive summary. ' +
  'The segments were summarized with the following instructions:\n\n';

/**
 * Generate the segment prompt from base prompt and flagged terms
 */
export function buildSegmentPrompt(
  basePrompt: string,
  flaggedTerms: string
): string {
  if (!flaggedTerms.trim()) {
    return basePrompt;
  }
  return `${basePrompt} Pay particular attention to and highlight any mentions of: ${flaggedTerms}.`;
}

/**
 * Generate the master prompt from segment prompt
 */
export function buildMasterPrompt(segmentPrompt: string): string {
  return MASTER_PROMPT_PREFIX + segmentPrompt;
}

/**
 * Generate prompt template with streamer name
 */
export function generatePromptTemplate(streamerName: string): string {
  return DEFAULT_PROMPT_TEMPLATE.replace('{STREAMER_NAME}', streamerName);
}

/**
 * Resolve the segment-prompt base + flagged terms that get handed to the
 * workflow, based on the form's prompt mode.
 *
 * - basic: generic summarization prompt with flagged topics appended
 *   (via buildSegmentPrompt downstream).
 * - advanced: the user's free-form prompt is used verbatim as the segment
 *   prompt; no flagged topics are appended. The master (combine) prompt is
 *   still auto-derived from it by buildMasterPrompt.
 */
export function resolvePromptInputs(params: {
  promptMode: 'basic' | 'advanced';
  customPrompt: string;
  promptTemplate: string;
  flaggedTerms: string;
  streamerName: string;
}): { promptTemplate: string; flaggedTerms: string } {
  if (params.promptMode === 'advanced') {
    return { promptTemplate: params.customPrompt.trim(), flaggedTerms: '' };
  }
  return {
    promptTemplate: params.promptTemplate || generatePromptTemplate(params.streamerName),
    flaggedTerms: params.flaggedTerms,
  };
}
